import { Client } from './client';
import { VelatirWatchDeniedError } from './exceptions';
import { WatchOptions } from './types';

let globalClient: Client | null = null;

export function getClient(): Client {
  if (!globalClient) {
    throw new Error('Velatir client is not initialized. Call velatir.init() first.');
  }
  return globalClient;
}

export function setClient(client: Client | null): void {
  globalClient = client;
}

interface WatchDecoratorOptions extends WatchOptions {}

export function watch(options: WatchDecoratorOptions = {}): MethodDecorator {
  return function <T>(
    target: unknown,
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<T>
  ): TypedPropertyDescriptor<T> | void {
    const originalMethod = descriptor.value as unknown;
    
    if (typeof originalMethod !== 'function') {
      throw new Error('@watch can only be applied to methods');
    }

    const isAsync = originalMethod.constructor.name === 'AsyncFunction';

    const wrappedMethod = async function (this: unknown, ...args: unknown[]) {
      const client = getClient();
      
      const functionName = propertyKey.toString();
      const doc = getFunctionDocstring(originalMethod as Function);
      
      const argsDict = convertArgsToDict(originalMethod as Function, args);
      
      const response = await client.createWatchRequest(
        functionName,
        argsDict,
        doc,
        options.metadata
      );

      if (response.isApproved) {
        if (isAsync) {
          return await (originalMethod as Function).apply(this, args);
        } else {
          return (originalMethod as Function).apply(this, args);
        }
      } else if (response.isPending) {
        const approval = await client.waitForApproval(
          response.requestId,
          (options.pollingInterval || 5) * 1000, // Convert to milliseconds
          options.maxAttempts
        );
        
        if (approval.isApproved) {
          if (isAsync) {
            return await (originalMethod as Function).apply(this, args);
          } else {
            return (originalMethod as Function).apply(this, args);
          }
        } else {
          throw new VelatirWatchDeniedError(approval.requestId);
        }
      } else {
        throw new VelatirWatchDeniedError(response.requestId);
      }
    };

    if (isAsync) {
      descriptor.value = wrappedMethod as T;
    } else {
      descriptor.value = (function (this: unknown, ...args: unknown[]) {
        // For sync functions, we still return a Promise since the wrapper is async
        return wrappedMethod.apply(this, args);
      }) as T;
    }

    return descriptor;
  };
}

function getFunctionDocstring(func: Function): string | undefined {
  const funcString = func.toString();
  const docMatch = funcString.match(/\/\*\*([\s\S]*?)\*\//);
  return docMatch ? docMatch[1].trim() : undefined;
}

function convertArgsToDict(func: Function, args: unknown[]): Record<string, unknown> {
  const funcString = func.toString();
  const paramMatch = funcString.match(/\(([^)]*)\)/);
  
  if (!paramMatch) {
    return {};
  }

  const paramStr = paramMatch[1];
  const paramNames = paramStr
    .split(',')
    .map(param => param.trim().split(/[\s:=]/)[0])
    .filter(name => name.length > 0);

  const argsDict: Record<string, unknown> = {};
  
  for (let i = 0; i < Math.min(paramNames.length, args.length); i++) {
    const paramName = paramNames[i];
    const value = args[i];
    
    if (typeof value === 'string' || 
        typeof value === 'number' || 
        typeof value === 'boolean' || 
        value === null || 
        value === undefined ||
        Array.isArray(value) ||
        (typeof value === 'object' && value !== null && value.constructor === Object)) {
      argsDict[paramName] = value;
    } else {
      try {
        if (value && typeof value === 'object' && 'toJSON' in value && typeof value.toJSON === 'function') {
          argsDict[paramName] = value.toJSON();
        } else if (value && typeof value === 'object') {
          argsDict[paramName] = { ...value as object };
        } else {
          argsDict[paramName] = String(value);
        }
      } catch {
        argsDict[paramName] = String(value);
      }
    }
  }

  return argsDict;
}